import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { MARKETS_CONFIG, MARKET_CODES } from "../../data/markets";

// Map IP country code (ISO-3166 alpha-2) to our market slug
const COUNTRY_TO_MARKET = {
  US: "us",
  GB: "uk",
  AU: "au",
  AE: "uae",
  CA: "ca",
  DE: "de",
  JP: "jp",
  SG: "sg",
  BR: "br",
  IN: "in",
};

const STORAGE_KEY = "lumotis_geo_prompt_v1";

export default function GeoBanner() {
  const [suggest, setSuggest] = useState(null); // market code to suggest
  const location = useLocation();

  useEffect(() => {
    // only show on routes that are NOT already a market route
    if (MARKET_CODES.includes(location.pathname.slice(1))) return;
    if (location.pathname !== "/") return;

    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) return;

    let cancelled = false;
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    // Same-origin backend proxy first (avoids CORS). ipapi.co kept as a soft fallback.
    const safeFetch = (p) =>
      p.then((r) => (r && r.ok ? r : Promise.reject())).catch(() => null);

    safeFetch(fetch(`${BACKEND}/api/geo`))
      .then(async (r) => {
        if (!r) return null;
        const j = await r.json();
        return j?.country || null;
      })
      .then(async (code) => {
        if (cancelled) return;
        if (!code) {
          // soft fallback
          try {
            const r2 = await fetch("https://ipapi.co/country/");
            if (r2.ok) code = (await r2.text()).trim();
          } catch {
            /* silent */
          }
        }
        const slug = COUNTRY_TO_MARKET[(code || "").trim().toUpperCase()];
        if (slug && !cancelled) setSuggest(slug);
      })
      .catch(() => {
        /* silent */
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  if (!suggest) return null;
  const m = MARKETS_CONFIG[suggest];

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setSuggest(null);
  };

  return (
    <div
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[60] w-[min(calc(100%-2rem),640px)] bg-charcoal text-white border border-electric shadow-xl shadow-electric/20 rounded-xl animate-[fadeIn_0.35s_ease-out]"
      role="dialog"
      aria-live="polite"
      data-testid="geo-banner"
    >
      <div className="flex items-start gap-4 p-5">
        <span className="text-3xl leading-none" aria-hidden>
          {m.flag}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-overline text-electric mb-1">
            Visiting from {m.name}?
          </p>
          <p className="font-display text-lg font-bold leading-tight">
            See Lumotis's local marketing playbook for {m.name}.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <Link
              to={`/${m.code}`}
              onClick={dismiss}
              data-testid={`geo-banner-accept-${m.code}`}
              className="inline-flex items-center gap-2 bg-electric text-white px-4 py-2 text-sm font-semibold hover:bg-white hover:text-charcoal transition-colors"
            >
              Take me there <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <button
              onClick={dismiss}
              data-testid="geo-banner-dismiss"
              className="text-xs text-zinc-400 hover:text-white underline-offset-4 hover:underline"
            >
              Stay on global
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          aria-label="Close"
          data-testid="geo-banner-close"
          className="text-zinc-400 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
